'use strict';

const BaseScene = require('./base-scene');
const Button = require('../ui/button');
const { COLORS, GAME_WIDTH, GAME_HEIGHT, MAX_LEVEL } = require('../config/constants');
const { drawGlowText, drawText, drawPanel, drawPlane } = require('../ui/draw');

class ResultScene extends BaseScene {
  enter(payload) {
    this.success = Boolean(payload.success);
    this.level = Math.max(1, Math.min(MAX_LEVEL, Number(payload.level) || 1));
    this.elapsed = Number(payload.elapsed) || 0;
    this.time = 0;
    this.isFinal = this.success && this.level === MAX_LEVEL;
    this.ranked = Boolean(payload.ranked);
    this.settlement = null;
    this.syncState = this.ranked ? 'loading' : 'practice';
    this.syncError = '';
    const token = (this.settlementToken || 0) + 1;
    this.settlementToken = token;
    if (payload.settlementPromise) {
      Promise.resolve(payload.settlementPromise).then((settlement) => {
        if (this.settlementToken !== token) return;
        this.settlement = settlement;
        this.syncState = settlement && settlement.queued ? 'queued' : (settlement && settlement.practice ? 'practice' : 'done');
      }).catch((error) => {
        if (this.settlementToken !== token) return;
        this.syncState = 'error';
        this.syncError = error.message || '成绩结算失败';
      });
    }
    this.createButtons();
  }

  createButtons() {
    const primaryLabel = this.success
      ? (this.isFinal ? '再次挑战' : '下一关')
      : '重新挑战';
    const targetLevel = this.success && !this.isFinal ? this.level + 1 : this.level;
    this.buttons = [
      new Button({
        x: 145,
        y: 870,
        width: 460,
        height: 96,
        label: primaryLabel,
        onTap: () => this.app.switchScene('game', { level: targetLevel })
      }),
      new Button({
        x: 145,
        y: 1000,
        width: 460,
        height: 88,
        label: '返回选关',
        variant: 'secondary',
        onTap: () => this.app.switchScene('levelSelect', {
          page: Math.floor((this.level - 1) / 15)
        })
      })
    ];
  }

  update(dt) {
    this.time += dt;
  }

  render(ctx) {
    if (this.success) this.drawSuccessParticles(ctx);
    if (this.isFinal) {
      this.drawFinal(ctx);
    } else {
      this.drawNormal(ctx);
    }
    this.drawButtons(ctx);
  }

  drawNormal(ctx) {
    const color = this.success ? COLORS.success : COLORS.danger;
    drawGlowText(ctx, this.success ? '任务完成' : '飞机失事', GAME_WIDTH / 2, 245, 68, color);
    if (this.success) drawPlane(ctx, GAME_WIDTH / 2, 410, 1.25, COLORS.success);
    else this.drawBrokenSignal(ctx, GAME_WIDTH / 2, 405);
    drawPanel(ctx, 80, 535, 590, 300, { stroke: color });
    drawText(ctx, `第 ${this.level} 关`, GAME_WIDTH / 2, 585, 33, COLORS.text, 'center', 'bold');
    drawText(
      ctx,
      this.success ? '已解锁新的航线' : `坚持了 ${this.elapsed.toFixed(1)} 秒`,
      GAME_WIDTH / 2,
      635,
      28,
      this.success ? COLORS.success : COLORS.muted
    );
    this.drawSettlement(ctx, 690);
  }

  drawFinal(ctx) {
    drawGlowText(ctx, '王牌飞行员', GAME_WIDTH / 2, 210, 72, '#ffd45d');
    ctx.save();
    ctx.translate(GAME_WIDTH / 2, 425);
    ctx.rotate(Math.sin(this.time) * 0.05);
    ctx.strokeStyle = '#ffd45d';
    ctx.fillStyle = 'rgba(255, 212, 93, 0.18)';
    ctx.lineWidth = 12;
    ctx.beginPath();
    for (let i = 0; i < 10; i += 1) {
      const radius = i % 2 === 0 ? 122 : 58;
      const angle = -Math.PI / 2 + i * Math.PI / 5;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    drawPlane(ctx, GAME_WIDTH / 2, 425, 1.15, '#ffd45d');
    drawPanel(ctx, 105, 590, 540, 195, { stroke: '#ffd45d' });
    drawText(ctx, '99 关全部通关', GAME_WIDTH / 2, 645, 36, '#ffd45d', 'center', 'bold');
    drawText(ctx, '星海因你而闪耀', GAME_WIDTH / 2, 706, 28, COLORS.text);
    drawText(ctx, `累计出击 ${this.app.profile.totalRuns} 次`, GAME_WIDTH / 2, 758, 24, COLORS.muted);
    this.drawSettlement(ctx, 825);
  }

  drawSettlement(ctx, y) {
    if (this.syncState === 'loading') {
      drawText(ctx, '成绩同步中…', GAME_WIDTH / 2, y, 25, COLORS.cyan, 'center', 'bold');
      return;
    }
    if (this.syncState === 'queued') {
      drawText(ctx, '排名待同步 · 联网后自动补交', GAME_WIDTH / 2, y, 23, COLORS.warning, 'center', 'bold');
      return;
    }
    if (this.syncState === 'practice') {
      drawText(ctx, '练习模式 · 本局不计入排名', GAME_WIDTH / 2, y, 23, COLORS.warning, 'center', 'bold');
      return;
    }
    if (this.syncState === 'error') {
      drawText(ctx, this.syncError, GAME_WIDTH / 2, y, 22, COLORS.danger);
      return;
    }
    const settlement = this.settlement || {};
    const run = settlement.run || {};
    drawText(ctx, `本局 ${run.score || 0} 分 · +${settlement.xpGained || 0} XP`, GAME_WIDTH / 2, y - 22, 25, COLORS.success, 'center', 'bold');
    const best = settlement.bestAllTime;
    const ranks = settlement.leaderboardRanks || {};
    drawText(ctx, `历史最佳 ${best ? best.score : run.score || 0} · 日榜 #${ranks.DAILY || '-'} · 周榜 #${ranks.WEEKLY || '-'}`, GAME_WIDTH / 2, y + 24, 20, COLORS.muted);
    const unlockCount = (settlement.newAchievements || []).length + (settlement.newCosmetics || []).length;
    if (unlockCount || (settlement.completedChallenges || []).length) {
      drawText(ctx, `新完成任务 ${(settlement.completedChallenges || []).length} · 新解锁 ${unlockCount}`, GAME_WIDTH / 2, y + 62, 20, '#ffd45d');
    }
  }

  drawSuccessParticles(ctx) {
    ctx.save();
    for (let i = 0; i < 28; i += 1) {
      const angle = (Math.PI * 2 * i) / 28 + this.time * 0.18;
      const radius = 210 + Math.sin(this.time * 2 + i) * 45;
      const x = GAME_WIDTH / 2 + Math.cos(angle) * radius;
      const y = 420 + Math.sin(angle) * radius * 0.65;
      ctx.fillStyle = i % 2 === 0 ? COLORS.cyan : '#ffd45d';
      ctx.globalAlpha = 0.35 + (i % 4) * 0.12;
      ctx.fillRect(x, y, 5, 12);
    }
    ctx.restore();
  }

  drawBrokenSignal(ctx, x, y) {
    ctx.save();
    ctx.strokeStyle = COLORS.danger;
    ctx.shadowColor = COLORS.danger;
    ctx.shadowBlur = 18;
    ctx.lineWidth = 14;
    ctx.beginPath();
    ctx.moveTo(x - 68, y - 68);
    ctx.lineTo(x + 68, y + 68);
    ctx.moveTo(x + 68, y - 68);
    ctx.lineTo(x - 68, y + 68);
    ctx.stroke();
    ctx.restore();
  }
}

module.exports = ResultScene;
